/**
 * Shared Formatting Utilities
 *
 * Common formatting functions used across all features
 */

/**
 * Format date string to localized format
 * @param dateString - ISO date string
 * @param format - Date format style
 * @returns Formatted date string
 */
export const formatDate = (
  dateString: string,
  format: "short" | "long" | "medium" = "short"
): string => {
  const date = new Date(dateString);

  switch (format) {
    case "long":
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    case "medium":
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    case "short":
    default:
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
  }
};

/**
 * Format date string as DD-MM-YYYY (natural/Indian format).
 * Accepts ISO date (YYYY-MM-DD) or any parseable date string.
 * @param dateString - Date string (e.g. YYYY-MM-DD)
 * @returns Formatted date string (dd-mm-yyyy) or original string if unparseable
 */
export const formatDateDDMMYYYY = (dateString: string | null | undefined): string => {
  if (dateString == null || dateString === "") return "";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    const d = date.getDate();
    const m = date.getMonth() + 1;
    const day = d < 10 ? "0" + d : String(d);
    const month = m < 10 ? "0" + m : String(m);
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateString;
  }
};

/**
 * Get currency symbol from currency code
 * @param currencyCode - Currency code (INR, USD, etc.)
 * @returns Currency symbol (₹, $, €, etc.)
 */
export const getCurrencySymbol = (currencyCode: string = "INR"): string => {
  try {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return formatter.format(0).replace(/\d/g, "").trim();
  } catch {
    // Fallback to common currency symbols
    const symbolMap: Record<string, string> = {
      INR: "₹",
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      AUD: "A$",
      CAD: "C$",
      CHF: "CHF",
      CNY: "¥",
      SGD: "S$",
    };
    return symbolMap[currencyCode.toUpperCase()] || currencyCode;
  }
};

/**
 * Format currency value
 * @param amount - Numeric amount
 * @param currencyCode - Currency code (INR, USD, etc.)
 * @param locale - Locale for formatting
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number,
  currencyCode: string = "INR",
  locale: string = "en-IN"
): string => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export type NumberFormatScale =
  | "whole"
  | "thousand"
  | "lakh"
  | "million"
  | "crore"
  | "billion";

export interface NumberFormatSetting {
  scale: NumberFormatScale;
  decimals: number;
  suffix: string;
  currency: string;
}

export const DEFAULT_NUMBER_FORMAT_SETTING: NumberFormatSetting = {
  scale: "whole",
  decimals: 0,
  suffix: "",
  currency: "INR",
};

const SCALE_DIVISORS: Record<NumberFormatScale, number> = {
  whole: 1,
  thousand: 1_000,
  lakh: 100_000,
  million: 1_000_000,
  crore: 10_000_000,
  billion: 1_000_000_000,
};

export const formatScaledCurrency = (
  amount: number | null | undefined,
  setting: NumberFormatSetting,
  currencySymbol: string = getCurrencySymbol(setting.currency)
): string => {
  if (amount === null || amount === undefined) return "—";
  const divisor = SCALE_DIVISORS[setting.scale] ?? 1;
  const scaled = amount / divisor;
  const formatted = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: setting.decimals,
    maximumFractionDigits: setting.decimals,
  }).format(scaled);
  const suffix = setting.scale === "whole" ? "" : setting.suffix;
  return `${currencySymbol}${formatted}${suffix ? ` ${suffix}` : ""}`;
};

/**
 * Format number with thousand separators
 * @param value - Numeric value
 * @param decimals - Number of decimal places
 * @returns Formatted number string
 */
export const formatNumber = (value: number, decimals: number = 2): string => {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Format file size to human-readable format
 * @param bytes - Size in bytes
 * @returns Formatted size string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Format percentage
 * @param value - Numeric value (0-100 or 0-1)
 * @param decimals - Number of decimal places
 * @param isDecimal - Whether value is in decimal format (0-1)
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number,
  decimals: number = 2,
  isDecimal: boolean = false
): string => {
  const percentage = isDecimal ? value * 100 : value;
  return `${percentage.toFixed(decimals)}%`;
};

/**
 * Format phone number
 * @param phone - Phone number string
 * @returns Formatted phone number
 */
export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, "");

  // Format for Indian mobile numbers (10 digits)
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{5})(\d{5})/, "$1 $2");
  }

  // Return as-is if not matching expected pattern
  return phone;
};

/**
 * Format GST number with spaces
 * @param gst - GST number
 * @returns Formatted GST string
 */
export const formatGST = (gst: string): string => {
  if (!gst || gst.length !== 15) return gst;

  // Format: XX XXXXX XXXX X XX
  return gst.replace(/(\w{2})(\w{5})(\w{4})(\w{1})(\w{3})/, "$1 $2 $3 $4 $5");
};

/**
 * Truncate text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text
 */
export const truncateText = (text: string, maxLength: number = 50): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};
